import {
  EUserAccessPart,
  EWorkspaceProjectRole,
  IProjectsResponseDto,
  IUserDto,
  IUserProjectRole,
  IWorkspaceProjectRole
} from "@kuika/kuika-cl-model";
import React from "react";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaDropdownMenu, { KuikaDropdownMenuItemType } from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallMediumText from "../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import ProjectDefinitionShareUser from "./project-definition-share-user";
import "./project-definition-share.scss";

const PUBLIC_ACCESS_OPTIONS: KuikaSelectboxOption[] = [
  { value: "private", label: "Restricted", testId: "restrictedOption" },
  { value: "public", label: "Everyone in workspace", testId: "everyoneInWorkspaceOption" }
];

export interface IState {
  searchValue: string;
  // Filters the added-user list. The search input is currently hidden; kept as a no-op.
  userListSearchValue: string;
}

export interface IProps {
  userList: IUserDto[];
  projectRoleList: IWorkspaceProjectRole[];
  selectedUsersToShare: IUserProjectRole[];
  addSelectedUserToShare: (user: IUserProjectRole) => void;
  removeSelectedUserToShare: (user: IUserProjectRole) => void;
  updateSelectedUsersToShareUserRole: (user: IUserProjectRole, roleId: string) => void;
  isPrivate: boolean;
  setPrivacity: (value: string) => void;
  projectOwnerId: string;
  projectToEdit: IProjectsResponseDto;
  handleLoadUsersList: () => Promise<void>;
  // While the candidate user list is still loading, show skeleton rows in the people
  // area instead of a momentarily empty owner row.
  isUserListLoading?: boolean;
}

class ProjectDefinitionShare extends React.PureComponent<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      searchValue: "",
      userListSearchValue: ""
    };
  }

  async componentDidMount() {
    await this.props.handleLoadUsersList();
  }

  getAddUserMenuOptions = (): KuikaDropdownMenuItemType[] => {
    const viewerId: string | undefined = this.props.projectRoleList.find(
      (role: IWorkspaceProjectRole) => role.name === "Viewer"
    )?.id;
    const filteredUsers: IUserDto[] | undefined = this.props.userList?.filter((user: IUserDto) =>
      this.state.searchValue ? user.username?.includes(this.state.searchValue) : true
    );

    return (filteredUsers ?? [])
      .filter(
        (user: IUserDto) =>
          !this.props.selectedUsersToShare.some((selectedUser: IUserProjectRole) => selectedUser.userId === user.id)
      )
      .filter((user: IUserDto) => this.props.projectOwnerId !== user.id)
      .map((user: IUserDto): KuikaDropdownMenuItemType => ({
        key: user.id,
        label: (
          <ProjectDefinitionShareUser
            user={user}
            withoutRole={true}
            testId={`shareUserOption-${user.username}`}
            projectRoleList={this.props.projectRoleList}
            removeSelectedUserToShare={this.props.removeSelectedUserToShare}
            projectToEdit={this.props.projectToEdit}
          />
        ),
        onClick: () => {
          this.props.addSelectedUserToShare({
            userId: user.id,
            projectRoleId: viewerId || ""
          });
        }
      }));
  };

  // Placeholder rows shown while the candidate user list loads. Mirrors the real share
  // row (avatar + name/email + role) so the list doesn't jump when data arrives.
  renderUserListSkeleton = (): React.ReactNode => {
    // Create: only the owner will appear → 1 row. Edit: match the project's existing
    // share-row count. projectToEdit.userProjectRoles is already loaded (same data used
    // for the project-list "Shared" avatars) — no extra fetch, so no perf cost.
    const isEditing: boolean = !!this.props.projectToEdit?.id;
    const skeletonCount: number = isEditing ? Math.max(1, this.props.projectToEdit?.userProjectRoles?.length ?? 1) : 1;
    return Array.from({ length: skeletonCount }).map((_, index: number) => (
      <div className="kuika-share__skeleton-row" key={`share-user-skeleton-${index}`} aria-hidden="true">
        <span className="kuika-share__skeleton-avatar" />
        <div className="kuika-share__skeleton-lines">
          <span className="kuika-share__skeleton-bar kuika-share__skeleton-bar--name" />
          <span className="kuika-share__skeleton-bar kuika-share__skeleton-bar--email" />
        </div>
        <span className="kuika-share__skeleton-bar kuika-share__skeleton-bar--role" />
      </div>
    ));
  };

  render() {
    const projectOwner = this.props.userList.find((user: IUserDto) => user.id === this.props.projectOwnerId);
    const hasAccessEditProjectRole = KMainFunctions.hasUserAccessInWorkspace(EUserAccessPart.EditProjectRole);
    const hasAccessEditProjectRoleInProject = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.EditProjectRole,
      this.props.projectToEdit.projectAccess
    );

    const hasAccessChangeProjectVisibility = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.ChangeProjectVisibility,
      this.props.projectToEdit.projectAccess
    );

    const addUserMenuOptions: KuikaDropdownMenuItemType[] = this.getAddUserMenuOptions();

    return (
      <div className="kuika-share">
        <div className="kuika-share__label">
          <KuikaSmallMediumText>SHARE</KuikaSmallMediumText>
        </div>

        <div className="kuika-share__body kuika-flex-column--gap-m">
          <div className="kuika-share__field">
            <KuikaSmallMediumText className="kuika-share__sublabel">People with access</KuikaSmallMediumText>
            {/* Click alone drives it, as antd's trigger did: an extra onFocus opener races rc-trigger's
                click toggle and closes the menu in the same gesture. */}
            <KuikaDropdownMenu
              options={addUserMenuOptions}
              overlayClassName="kuika-share__search-overlay"
              emptyState={<div className="kuika-share__dropdown-empty">No results found</div>}
              emptyStateCondition={addUserMenuOptions.length === 0}
            >
              <KuikaInput
                data-testid="addUserInput"
                size="sm"
                className="kuika-share__search"
                leftIcon={<KuikaSvg name="search" width={12} height={12} color="var(--dark-6)" />}
                placeholder="Add or search by name or email address.."
                value={this.state.searchValue}
                onChange={(_e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string | number) =>
                  this.setState({ searchValue: String(value ?? "") })
                }
              />
            </KuikaDropdownMenu>
          </div>

          <div className="kuika-share__users">
            {this.props.isUserListLoading && this.renderUserListSkeleton()}
            {projectOwner &&
              (hasAccessEditProjectRole || hasAccessEditProjectRoleInProject) &&
              (this.state.userListSearchValue && this.state.userListSearchValue !== ""
                ? projectOwner.username?.includes(this.state.userListSearchValue) ||
                  projectOwner.firstName?.includes(this.state.userListSearchValue) ||
                  projectOwner.lastName?.includes(this.state.userListSearchValue)
                : true) && (
                <ProjectDefinitionShareUser
                  withoutRole={false}
                  user={projectOwner}
                  testId={`shareUserRow-${projectOwner.username}`}
                  projectRole={
                    this.props.projectRoleList.find(
                      (role: IWorkspaceProjectRole) => role.enumId === EWorkspaceProjectRole.ProjectOwner
                    ) || undefined
                  }
                  projectRoleList={this.props.projectRoleList}
                  removeSelectedUserToShare={this.props.removeSelectedUserToShare}
                  updateSelectedUsersToShareUserRole={this.props.updateSelectedUsersToShareUserRole}
                  projectToEdit={this.props.projectToEdit}
                />
              )}
            {this.props.selectedUsersToShare?.map((role: IUserProjectRole) => {
              const user = this.props.userList.find((user: IUserDto) => user.id === role.userId);
              if (this.state.userListSearchValue && this.state.userListSearchValue !== "") {
                if (
                  !user?.username?.includes(this.state.userListSearchValue) &&
                  !user?.firstName?.includes(this.state.userListSearchValue) &&
                  !user?.lastName?.includes(this.state.userListSearchValue)
                ) {
                  return null;
                }
              }
              const projectRole: IWorkspaceProjectRole | undefined = this.props.projectRoleList.find(
                (projectRole: IWorkspaceProjectRole) => projectRole.id === role.projectRoleId
              );
              if (user?.id === this.props.projectOwnerId) {
                return null;
              }
              return user ? (
                <ProjectDefinitionShareUser
                  key={role.userId}
                  withoutRole={false}
                  user={user}
                  testId={`shareUserRow-${user.username}`}
                  projectRole={projectRole}
                  projectRoleList={this.props.projectRoleList}
                  removeSelectedUserToShare={this.props.removeSelectedUserToShare}
                  updateSelectedUsersToShareUserRole={this.props.updateSelectedUsersToShareUserRole}
                  projectToEdit={this.props.projectToEdit}
                />
              ) : null;
            })}
          </div>
        </div>

        {hasAccessChangeProjectVisibility && (
          <div className="kuika-share__access">
            <KuikaSmallMediumText className="kuika-share__sublabel">Public access</KuikaSmallMediumText>
            <div className="kuika-share__access-row">
              <KuikaSelectbox
                data-testid="publicAccessDropdown"
                options={PUBLIC_ACCESS_OPTIONS}
                onChange={(value: string) => this.props.setPrivacity(value)}
                value={this.props.isPrivate ? "private" : "public"}
              />
              <span data-testid="publicAccessDescription" className="kuika-share__access-description">
                {this.props.isPrivate
                  ? "It can only be accessed by people with whom the project is shared."
                  : "It can be viewed by all users registered in Workspace."}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default ProjectDefinitionShare;
